import React, {
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import type {
  MyGreenhouseProps,
  MyGreenhousePaths,
} from 'talent_matching/types';

export interface MyGreenhouseTabContextValue {
  paths: MyGreenhousePaths;
  savedSearchOptions: Item[];
  setSavedSearchOptions: (options: SetStateAction<Item[]>) => void;
  savedFilterSet: Item | null;
  setSavedFilterSet: (item: Item | null) => void;
}

const MyGreenhouseTabContext = createContext<
  MyGreenhouseTabContextValue | undefined
>(undefined);

export const useMyGreenhouseTabContext = () => {
  const context = useContext(MyGreenhouseTabContext);
  if (!context) {
    throw new Error(
      'useMyGreenhouseTabContext must be used within a MyGreenhouseTabContextProvider'
    );
  }
  return context;
};

type ProviderProps = {
  myGreenhouseProps: MyGreenhouseProps;
  children: React.ReactNode;
};

export const MyGreenhouseTabContextProvider = ({
  myGreenhouseProps,
  children,
}: ProviderProps) => {
  const [savedFilterSet, setSavedFilterSet] = useState<Item | null>(null);
  const [savedSearchOptions, setSavedSearchOptions] = useState<Item[]>(
    () =>
      myGreenhouseProps.savedSearchOptions?.map((o) => ({
        label: o.label,
        value: o.value,
      })) ?? []
  );

  const contextValue = useMemo(
    () => ({
      paths: myGreenhouseProps.paths,
      savedSearchOptions,
      setSavedSearchOptions,
      savedFilterSet,
      setSavedFilterSet,
    }),
    [myGreenhouseProps.paths, savedSearchOptions, savedFilterSet]
  );

  return (
    <MyGreenhouseTabContext.Provider value={contextValue}>
      {children}
    </MyGreenhouseTabContext.Provider>
  );
};
